<template>
  <div class="d-flex flex-column align-center justify-center">

    <error-dialog :status="loadStripeCardElementStatus" />
    <error-dialog :status="submitPaymentStatus" />
    <error-dialog :status="submitOfflinePaymentStatus" />

    <dialog-scroll-lock
      v-if="showCardDialog"
      width="400" transition=""
      :persistent="submitPaymentStatus.pending"
      v-model="showCardDialog"
    >
      <v-card>
        <v-btn
          class="ma-3" style="position: absolute; right: 0;"
          icon
          :disabled="submitPaymentStatus.pending"
          @click="showCardDialog = false"
        >
          <v-icon v-html="mdiClose" />
        </v-btn>
        <v-card-title class="d-flex justify-center py-6">
          <price class="text-h4" :value="itemized.price" />
        </v-card-title>
        <v-card-text>
          <div class="relative">
            <v-text-field
              dense outlined readonly label="Card" hide-details persistent-placeholder
              :error="!!cardError" :error-messages="cardError ? [ cardError ] : []"
              :loading="loadStripeCardElementStatus.pending"
            />
            <div ref="card-element-dialog" class="mx-3 mt-3" style="position: absolute; top: 0; left: 0; right: 0;" />
          </div>
          <div id="card-errors" role="alert" />
          <div class="mt-6 d-flex flex-wrap align-center justify-end">
            <v-checkbox class="my-2 pt-0" hide-details label="Save card" :disabled="forceSaveCard" v-model="saveCard" />
            <v-spacer />
            <div>
              <v-btn class="mx-2" :disabled="submitPaymentStatus.pending" @click="showCardDialog = false">Cancel</v-btn>
              <v-btn
                color="primary"
                :disabled="!isCardComplete" :loading="submitPaymentStatus.pending"
                @click="submitPayment({ card })"
              >
                Pay now
              </v-btn>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </dialog-scroll-lock>

    <div
      v-if="supportedProcessors.STRIPE_CARD && !supportedProcessors.PAYPAL && !(supportedProcessors.STRIPE_EXTERNAL && canMakeExternalStripePayment) && !savedPaymentMethods.length"
      v-show="!loadSavedPaymentMethodsStatus.pending"
      class="fill-width"
    >
      <!-- The only option we can take is to add a new card - save a click and just show the interface here -->
      <div class="relative">
        <v-text-field
          class="white"
          dense outlined readonly label="Card" hide-details persistent-placeholder
          :error="!!cardError" :error-messages="cardError ? [ cardError ] : []"
          :loading="loadStripeCardElementStatus.pending"
        />
        <div ref="card-element-inline" class="mx-3 mt-3" style="position: absolute; top: 0; left: 0; right: 0;" />
      </div>
      <div id="card-errors" role="alert" />
      <div class="mt-6 d-flex align-center">
        <v-checkbox class="my-2 pt-0" hide-details label="Save card" :disabled="forceSaveCard" v-model="saveCard" />
        <v-spacer />
        <v-btn
          color="primary"
          :disabled="!isCardComplete" :loading="submitPaymentStatus.pending"
          @click="submitPayment({ card })"
        >
          Pay now
        </v-btn>
      </div>
    </div>

    <v-progress-circular v-else-if="loadExternalPaymentRequestButtonStatus.pending" class="mx-auto" indeterminate />

    <div v-else class="my-n3" style="width: 200px;">
      <template v-if="supportedProcessors.STRIPE_CARD">
        <div v-if="savedPaymentMethods.length" class="my-3 btn-group d-flex">
          <v-btn
            class="btn-group-right flex-grow-1"
            :loading="submitOfflinePaymentStatus.pending"
            @click="submitOfflinePayment(savedPaymentMethod.id)"
          >
            <credit-card-preview class="text-body-2" style="text-transform: none;" :card="savedPaymentMethod.card" />
          </v-btn>
          <v-menu left offset-y nudge-right="6">
            <template v-slot:activator="{ on, attrs }">
              <v-btn class="btn-group-left px-2" v-bind="attrs" v-on="on">
                <v-icon v-html="mdiMenuDown" />
              </v-btn>
            </template>
            <v-list class="py-0 text-body-2" style="width: 180px;">
              <v-list-item
                v-for="item in savedPaymentMethods" :key="item.id"
                class="simple" style="min-height: unset;"
                @click="submitOfflinePayment(item.id)"
              >
                <div>
                  <credit-card-preview :card="item.card" />
                  <div v-if="item.description" class="text--label-active">{{item.description}}</div>
                </div>
              </v-list-item>
              <v-divider />
              <v-list-item class="simple primary--text" style="min-height: unset;" @click="showCardDialog = true">
                <v-icon class="mr-3" v-html="mdiCreditCard" />
                <div>New card…</div>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
        <div v-else class="my-3">
          <v-btn width="200" @click="showCardDialog = true">
            <v-icon class="mr-1" v-html="mdiCreditCard" /> Card
          </v-btn>
        </div>
      </template>

      <paypal
        v-if="supportedProcessors.PAYPAL"
        v-bind="$props"
        class="my-3"
        @metadata-error="e => $emit('metadata-error', e)"
        @complete="onComplete"
        @price-update="$emit('price-update')"
      />

      <div
        v-if="supportedProcessors.STRIPE_EXTERNAL && canMakeExternalStripePayment"
        class="my-3 v-btn v-btn--is-elevated align-center overflow-hidden d-block"
        style="height: 36px; max-height: 36px;"
      >
        <div ref="payment-request" class="fill-width" />
      </div>
    </div>

  </div>
</template>


<script>
import CreditCardPreview from '@/components/CreditCardPreview.vue';
import Paypal from '@/components/Paypal.vue';
import Price from '@/components/Price.vue';
import SignInDialog from '@/components/SignInDialog.vue';
import { LOCALE, TZ } from '@/utils/intl.js';
import loadScript from '@/utils/load-script.js';
import { recordPurchase } from '@/utils/purchase-metrics.js';
import { mdiAccount, mdiClose, mdiCreditCard, mdiMenuDown, mdiInformation, mdiCurrencyUsd, mdiChevronRight } from '@mdi/js';


export default {
  name: 'payment-btns',

  components: {
    CreditCardPreview,
    Paypal,
    Price,
    SignInDialog
  },

  props: [ 'itemized', 'metadata' ],

  data() {
    return {
      savedPaymentMethod: null,
      savedPaymentMethods: [],
      card: null,
      isCardComplete: false,
      cardError: null,
      showCardDialog: false,
      canMakeExternalStripePayment: null,
      showSignIn: false,
      saveCard: true,
      loadStripeCardElementStatus: this.$track('loadStripeCardElement'),
      loadSavedPaymentMethodsStatus: this.$track('loadSavedPaymentMethods'),
      loadExternalPaymentRequestButtonStatus: this.$track('loadExternalPaymentRequestButton'),
      submitPaymentStatus: this.$track('submitPayment'),
      submitOfflinePaymentStatus: this.$track('submitOfflinePayment')
    };
  },

  computed: {
    supportedProcessors() {
      const allowed = { STRIPE_CARD: true, PAYPAL: true, STRIPE_EXTERNAL: true };
      for (const item of this.itemized.items) {
        if (item.key.startsWith('SUBSCRIPTION')) {
          allowed.PAYPAL = false;
          allowed.STRIPE_EXTERNAL = false;
        } else if (item.key.startsWith('GIFTCARD')) {
          allowed.PAYPAL = false;
          allowed.STRIPE_EXTERNAL = false;
        }
      }
      return allowed;
    },

    forceSaveCard() {
      return !!this.itemized.items.find(x => x.key.startsWith('SUBSCRIPTION'));
    }
  },

  created() {
    Object.assign(this, { mdiAccount, mdiClose, mdiCreditCard, mdiMenuDown, mdiInformation, mdiCurrencyUsd, mdiChevronRight });
    window._stripeFrames = (window._stripeFrames || 0) + 1;
  },

  mounted() {
    this.loadStripeCardElement();
  },

  async beforeDestroy() {
    // Stripe leaves around lots of iframes that it shouldn't. There's not a good mechanism for removing these and
    // their listeners, but this seems to work.
    // https://github.com/stripe-archive/react-stripe-elements/issues/99
    // https://gist.github.com/brndnmtthws/c5b28592c321533b8aa6971212295045
    const stripe = await loadStripePromise;
    window._stripeFrames--;
    if (!window._stripeFrames) {
      // Note that we're careful not to remove any frames unless this is the only instance
      for (const frame of Object.values(stripe._controller._frames)) {
        frame._iframe.remove();
        frame._removeAllListeners();
      }
    }
  },

  methods: {
    async loadExternalPaymentRequestButton() {
      if (this.itemized.price == '0.00' || !this.supportedProcessors.STRIPE_EXTERNAL)
        return;

      // https://stripe.com/docs/stripe-js/elements/payment-request-button
      const stripe = await loadStripePromise;

      this.canMakeExternalStripePayment = null;
      this.externalPaymentRequest = stripe.paymentRequest({
        country: 'US',
        currency: 'usd',
        total: {
          label: 'Total',
          amount: parseInt(this.itemized.price.replace('.', ''))
        }
      });

      this.externalPaymentRequest.on('cancel', console.log);

      this.externalPaymentRequest.on(
        'paymentmethod',
        async e => {
          this.saveCard = false;
          try {
            await this.submitPayment(e.paymentMethod.id);
            e.complete('success');
          } catch (e) {
            e.complete('fail');
          }
        }
      );

      this.canMakeExternalStripePayment = await this.externalPaymentRequest.canMakePayment();
      await this.loadSavedPaymentMethodsStatus.promise;

      if (this.canMakeExternalStripePayment)
        setTimeout(() => stripe.elements().create('paymentRequestButton', { paymentRequest: this.externalPaymentRequest }).mount(this.$refs['payment-request']));
    },

    async loadStripeCardElement() {
      const stripe = await loadStripePromise;
      const elMount = this.$refs['card-element-inline'] || this.$refs['card-element-dialog'];
      if (!elMount)
        return;
      const computed = getComputedStyle(this.$el, null);
      const style = {
        base: {
          color: computed.getPropertyValue('color'),
          fontFamily: computed.getPropertyValue('font-family'),
          fontSize: computed.getPropertyValue('font-size')
        },
        invalid: {
          color: this.$vuetify.theme.defaults.light.error
        }
      };
      this.card = stripe.elements().create('card', { style });
      this.isCardComplete = false;
      this.cardError = null;
      this.card.on('change', e => {
        this.isCardComplete = e.complete;
        this.cardError = e.error ? e.error.message : null;
      });
      this.card.mount(elMount);
    },

    async checkSubmitPayment(params, offline) {
      // Errors are standardized across both of these APIs
      const api = offline ? 'stripe/complete-offline-payment' : 'stripe/create-payment-intent';

      // DEBUG
      $event.log('api-call', { api, data: params });

      let response;
      try {
        response = await this.$call(api, params);
        if (response.price_update) {
          this.$emit('price-update');
          return null;
        }
        if (response.confirm) {
          const r = await this.$confirm({
            title: 'Are you sure?',
            msg: response.confirm,
            buttons: [ 'Cancel', 'Continue' ],
            primary: 'Cancel'
          });
          if (r == 'Cancel')
            return null;
          return await this.checkSubmitPayment({ ...params, skip_duplicate_check: true }, offline);
        }
        if (response.error)
          throw new VisibleError(response.error);
        if (response.card_error)
          throw new VisibleError(response.card_error);
      } catch (e) {
        if (e.type == 'LockedPaymentIntent')
          throw new VisibleError('Sorry, this account has been locked - please contact us: <a href="mailto:support@ellacard.com">support@ellacard.com</a>');
        if (e.type == 'MultipleFailedPaymentIntents')
          throw new VisibleError('Sorry, this account has been temporarily locked due to too many incomplete payment attempts - please contact us: <a href="mailto:support@ellacard.com">support@ellacard.com</a>');
        if (e.type == 'InvalidMetadataException') {
          this.$emit('metadata-error', e);
          return null;
        }
        await this.$call('record-failed-payment-intent');
        throw e;
      }
      return response;
    },

    async submitPayment(payment_method) {
      const params = { metadata: this.metadata, locale: LOCALE, tz: TZ, items: this.itemized.items, save: this.saveCard };
      const response = await this.checkSubmitPayment(params, false);
      if (!response)
        return;
      const stripe = await loadStripePromise;
      const confirmed = await stripe.confirmCardPayment(response.client_secret, { payment_method });
      try {
        if (confirmed.error)
          throw new VisibleError(confirmed.error.message);
        if (confirmed.paymentIntent.status != 'succeeded')
          throw new Error('Unknown');
      } catch (e) {
        // TODO: it would be better to call this via some webhook so it can't be blocked by nefarious users
        await this.$call('record-failed-payment-intent');
        throw e;
      }

      // DEBUG
      $event.log('api-call', { api: 'stripe/complete-payment-intent', data: { payment_intent_id: confirmed.paymentIntent.id } });

      const id = await this.$call('stripe/complete-payment-intent', { payment_intent_id: confirmed.paymentIntent.id });
      this.showCardDialog = false;
      this.onComplete(id);
    },

    async submitOfflinePayment(payment_method_id) {
      const params = { metadata: this.metadata, locale: LOCALE, items: this.itemized.items, payment_method_id };
      const response = await this.checkSubmitPayment(params, true);
      if (response)
        this.onComplete(response.payment_id);
    },

    async loadSavedPaymentMethods() {
      this.savedPaymentMethods = [];
      this.savedPaymentMethod = null;
      try {
        const r = await this.$call('stripe/list-payment-methods');
        this.savedPaymentMethods = r.payment_methods;
        this.savedPaymentMethod = r.payment_methods.find(x => x.id == r.primary);
      } catch (e) {
        // Should not block anything
      }
    },

    onComplete(id) {
      const exclude = [ 'GIFTCARD', 'GIFTCARD_BATCH', 'CREDIT_GIFTCARD' ];
      let realizedI = parseInt(this.itemized.price.replace('.', ''));
      for (const item of this.itemized.items)
        if (exclude.includes(item.key))
          realizedI -= item.qty * parseInt(item.unit.replace('.', ''));
      const realized = Math.max(0, (realizedI / 100).toFixed(2));
      if (id)
        // This event can get triggered without an ID for payments that fail the idempotency check
        recordPurchase(id, realized);
      this.$emit('complete', id);
    }
  },

  watch: {
    globalUserId: {
      immediate: true,
      handler() {
        this.loadSavedPaymentMethods();
      }
    },

    showCardDialog(v) {
      if (v)
        this.$nextTick(this.loadStripeCardElement);
    },

    itemized: {
      immediate: true,
      handler() {
        this.loadExternalPaymentRequestButton();
      }
    },

    forceSaveCard: {
      immediate: true,
      handler(v) {
        if (v)
          this.saveCard = true;
      }
    },

    'submitPaymentStatus.pending'(v) {
      this.card && this.card.update({ disabled: v });
    },

    'loadExternalPaymentRequestButtonStatus.pending': {
      immediate: true,
      handler(v) {
        this.$emit('update:loading', v);
      }
    }
  }
}
</script>
